import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import {
  Loader2,
  AlertTriangle,
  ShieldAlert,
  ExternalLink,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useStripeStatus } from "@/hooks/useStripeStatus";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

interface StripeError {
  error?: string;
  error_description?: string;
  details?: string;
  statusCode?: number;
  errorData?: any;
}

const MAX_AUTH_RETRIES = 3;
const AUTH_RETRY_DELAY = 1500;

const StripeCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { checkConnection, clearCache } = useStripeStatus();
  const [callbackExecuted, setCallbackExecuted] = useState(false);
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [stripeError, setStripeError] = useState<StripeError | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [authRetries, setAuthRetries] = useState(0);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [currentSession, setCurrentSession] = useState<any>(null);

  useEffect(() => {
    // Function to check authentication status
    const checkAuth = async () => {
      if (authRetries >= MAX_AUTH_RETRIES) {
        console.error(`Max auth retries (${MAX_AUTH_RETRIES}) reached`);
        setStatus("error");
        setErrorMessage(
          `Authentication failed after ${MAX_AUTH_RETRIES} attempts`
        );
        return;
      }

      try {
        const { data, error } = await supabase.auth.getSession();

        if (error || !data.session) {
          setAuthRetries((prev) => prev + 1);
          setTimeout(checkAuth, AUTH_RETRY_DELAY);
          return;
        }

        setSessionToken(data.session.access_token);
        setCurrentSession(data.session);
        setAuthChecked(true);
      } catch (err: any) {
        console.error("[Stripe Debug] Auth check error:", err);
        setAuthRetries((prev) => prev + 1);
        if (authRetries >= MAX_AUTH_RETRIES - 1) {
          setStatus("error");
          setErrorMessage("Authentication error: " + err.message);
        } else {
          setTimeout(checkAuth, AUTH_RETRY_DELAY);
        }
      }
    };

    if (!authChecked && !callbackExecuted) {
      checkAuth();
    }
  }, [authRetries, authChecked, callbackExecuted]);

  useEffect(() => {
    const hasOAuthParams = () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      return !!code && !!state;
    };

    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      if (!code || !state) {
        setStatus("error");
        setErrorMessage("Missing required parameters from Stripe");
        return;
      }

      try {
        console.log(
          "[Stripe Debug] Calling OAuth callback with code and state"
        );

        // Add debug info for the redirect URL issue
        const currentUrl = window.location.href;
        setDebugInfo(
          `Current URL: ${currentUrl}\nRedirect should be configured in Stripe Dashboard as: ${
            currentUrl.split("?")[0]
          }`
        );

        const { data, error } = await supabase.functions.invoke(
          "connect-stripe",
          {
            body: {
              action: "oauth_callback",
              code,
              state,
            },
            headers: {
              Authorization: `Bearer ${sessionToken}`,
            },
          }
        );

        if (error || !data.success) {
          console.error(
            "[Stripe Debug] Stripe connection failed:",
            error || data.error
          );
          setStatus("error");
          setErrorMessage(error?.message || data.error || "Connection failed");
          setStripeError(data.errorData || { error: "connection_failed" });
        } else {
          console.log("[Stripe Debug] Stripe connection successful:", data);
          
          // Set flag to show fork modal in dashboard
          sessionStorage.setItem('showStripeForkModal', 'true');

          // Clear URL parameters
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );

          // Clear any cached connection status
          clearCache();

          // Refresh connection status bypassing cache
          await checkConnection(true);

          // Use the stored session from currentSession instead of sessionData
          // Check the account ID was actually saved
          if (currentSession && currentSession.user && currentSession.user.id) {
            const { data: profile, error: profileError } = await supabase
              .from("profiles")
              .select("stripe_account_id")
              .eq("id", currentSession.user.id)
              .single();

            console.log("[Stripe Debug] Profile check after callback:", {
              accountId: profile?.stripe_account_id,
              error: profileError,
              userId: currentSession.user.id,
            });

            // If profile fetch was successful but no account ID was found, show an error
            if (!profileError && (!profile || !profile.stripe_account_id)) {
              console.error(
                "[Stripe Debug] Account ID was not properly saved in database"
              );
              setStatus("error");
              setErrorMessage(
                "Your Stripe account was connected but the account ID wasn't properly saved. Please try again."
              );
              setStripeError({
                error: "account_id_not_saved",
                error_description:
                  "The connection was successful but the account ID wasn't properly saved in your profile.",
              });
              return;
            }
          } else {
            console.error(
              "[Stripe Debug] No valid user session available to check profile"
            );
          }

          // Wait briefly to ensure database updates are complete
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Dispatch an event to notify the app that Stripe has been connected
          const stripeConnectedEvent = new CustomEvent("stripe-connected", {
            detail: { accountId: currentSession?.user?.id },
          });
          document.dispatchEvent(stripeConnectedEvent);
          // Redirect to dashboard immediately
          setStatus("success");
          navigate("/dashboard", { replace: true });
        }
      } catch (error: any) {
        console.error("[Stripe Debug] Unexpected error:", error);
        setStatus("error");
        setErrorMessage(error.message);
        setStripeError({
          error: "unexpected_error",
          error_description: error.message,
        });
      }
    };

    if (authChecked && sessionToken && !callbackExecuted && hasOAuthParams()) {
      setCallbackExecuted(true);
      handleCallback();
    }
  }, [
    authChecked,
    sessionToken,
    callbackExecuted,
    searchParams,
    navigate,
    toast,
    checkConnection,
    clearCache,
    currentSession,
  ]);

  // Add more detailed error instructions for the redirect URI issue
  const renderStripeErrorDetails = () => {
    if (!stripeError) return null;

    let redirectUriError = false;
    if (
      stripeError.error_description?.includes("redirect URI") ||
      stripeError.details?.includes("redirect URI") ||
      stripeError.error?.includes("redirect URI")
    ) {
      redirectUriError = true;
    }

    return (
      <div className="mt-4 space-y-4">
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Stripe Error Details</AlertTitle>
          <AlertDescription>
            {stripeError.error_description ||
              stripeError.error ||
              "Unknown error"}
            {stripeError.details && (
              <div className="mt-2 text-sm">Details: {stripeError.details}</div>
            )}
            {stripeError.statusCode && (
              <div className="mt-2 text-sm">
                Status code: {stripeError.statusCode}
              </div>
            )}
          </AlertDescription>
        </Alert>

        {redirectUriError && (
          <div className="text-sm text-gray-600 mt-2">
            <p className="font-medium">Redirect URI Mismatch:</p>
            <ul className="list-disc pl-5 mt-1">
              <li>
                The redirect URI in your code:{" "}
                <code>{window.location.origin}/stripe/callback</code>
              </li>
              <li>
                This must match <strong>exactly</strong> what's configured in
                your Stripe Dashboard
              </li>
              <li>
                Go to the Stripe Dashboard &gt; Connect &gt; Settings to update
                the allowed redirect URIs
              </li>
              <li>
                Make sure to add both your production URL and development URL
                (if needed)
              </li>
            </ul>
          </div>
        )}

        {stripeError.error === "invalid_grant" && (
          <div className="text-sm text-gray-600 mt-2">
            <p className="font-medium">This may be because:</p>
            <ul className="list-disc pl-5 mt-1">
              <li>The authorization code has already been used</li>
              <li>The authorization code has expired</li>
              <li>
                The redirect URI does not match the one used during
                authorization
              </li>
            </ul>
          </div>
        )}

        <div className="text-sm text-gray-500 mt-4">
          <p>Common solutions:</p>
          <ul className="list-disc pl-5 mt-1">
            <li>Try connecting again from the settings page</li>
            <li>Ensure your Stripe account is properly set up</li>
            <li>
              Check that STRIPE_CLIENT_ID and STRIPE_SECRET_KEY are correctly
              configured in your Supabase Edge Function secrets
            </li>
            <li>
              Verify the redirect URI is correct and matches in both your Stripe
              dashboard and code
            </li>
          </ul>
        </div>

        {debugInfo && (
          <div className="mt-4">
            <details className="text-xs">
              <summary className="cursor-pointer text-sm text-gray-600">
                Debug Information
              </summary>
              <pre className="mt-2 bg-gray-100 p-3 rounded overflow-x-auto">
                {debugInfo}
              </pre>
            </details>
          </div>
        )}
      </div>
    );
  };

  const handleRetryConnection = async () => {
    try {
      localStorage.removeItem("stripeConnected");
      localStorage.removeItem("stripeAccountId");
      localStorage.removeItem("stripeStatusTimestamp");

      await supabase.auth.refreshSession();

      navigate("/dashboard?tab=settings", { replace: true });
    } catch (error: any) {
      console.error("[Stripe Debug] Error during retry:", error);
      toast({
        title: "Retry Failed",
        description: "Failed to prepare for retry: " + error.message,
        variant: "destructive",
      });
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Card className="w-full max-w-4xl p-8">
        {(status === "processing" || status === "success") && (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-12 w-12 animate-spin text-keepmore-purple mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {status === "success" ? "Redirecting..." : "Connecting to Stripe"}
            </h2>
            <p className="text-gray-500 text-center">
              {status === "success"
                ? "Taking you to your dashboard..."
                : !authChecked
                ? `Verifying your authentication... (Attempt ${
                    authRetries + 1
                  }/${MAX_AUTH_RETRIES})`
                : "Please wait while we complete your Stripe connection..."}
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-col items-center justify-center py-8">
            <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Connection Error</h2>
            <p className="text-gray-500 text-center mb-4">
              {errorMessage ||
                "An unexpected error occurred. Please try again."}
            </p>
            {renderStripeErrorDetails()}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <Button
                onClick={() => navigate("/dashboard?tab=settings")}
                variant="outline"
              >
                Return to Settings
              </Button>
              <Button onClick={handleRetryConnection} variant="default">
                Retry Connection
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default StripeCallback;
